// PATH: src/pages/PedagogyAdminPage.tsx — configuration pédagogique :
// matières, salles, trimestres du bulletin, coefficients par niveau.
//
// Écran de RÉGLAGE, séparé de Pédagogie qui porte le travail quotidien
// (emploi du temps, notes, assiduité). On y vient rarement, pour préparer
// l'année — pas tous les jours.
//
// Les enseignants ne sont PAS ici : ils vivent dans Personnel, qui gère la même
// table (fonctions, congés, comptes liés). Deux éditeurs pour une même donnée
// finissent toujours par diverger.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Col, Empty, Form, Input, InputNumber, List, Popconfirm, Row,
  Select, Space, Tooltip, Typography, message,
} from "antd";
import { LockOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import { usePedagogyApi } from "../api/pedagogy";
import PageHeader from "../components/PageHeader";
import ProgrammeTag from "../components/ProgrammeTag";
import { useSchoolContext } from "../hooks/useSchoolContext";
import { FONT_MONO } from "../theme";
import type { Level, SchoolYear } from "../models/school";

const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée");

/**
 * Création en ligne d'un réglage (matière, salle, trimestre).
 *
 * Confirme l'enregistrement et vide les champs. Sans cela, la ligne créée
 * s'ajoutait en bas d'une liste qui dépasse souvent l'écran pendant que la
 * saisie restait affichée à l'identique : rien ne distinguait « c'est
 * enregistré » de « le clic n'a rien fait ».
 */
const InlineCreate: React.FC<{
  /** Libellé du bouton. */
  label: string;
  /** Confirmation, à la forme accomplie : « Matière ajoutée ». */
  created: string;
  onFinish: (v: any) => Promise<any>;
  onDone: () => void;
  children: React.ReactNode;
}> = ({ label, created, onFinish, onDone, children }) => {
  const [form] = Form.useForm();
  return (
    <Form
      form={form}
      layout="inline"
      style={{ marginBottom: 12 }}
      onFinish={(v) =>
        onFinish(v)
          .then(() => {
            message.success(created);
            form.resetFields();
            onDone();
          })
          .catch(apiError)
      }
    >
      {children}
      <Button htmlType="submit" size="small">{label}</Button>
    </Form>
  );
};

const PedagogyAdminPage: React.FC = () => {
  const api = usePedagogyApi();
  const school = useSchoolApi();

  const [years, setYears] = useState<SchoolYear[]>([]);
  // Même année de travail que les autres écrans (useSchoolContext).
  const { yearId, setYearId } = useSchoolContext();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [terms, setTerms] = useState<any[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);

  useEffect(() => {
    school.listYears().then((ys) => {
      setYears(ys);
      // Année mémorisée conservée si elle existe encore, sinon la plus récente.
      setYearId((prev) => (prev && ys.some((y) => y.id === prev) ? prev : ys[0]?.id));
    }).catch(() => undefined);
  }, [school, setYearId]);

  const load = useCallback(() => {
    api.listSubjects().then(setSubjects).catch(() => undefined);
    api.listRooms().then(setRooms).catch(() => undefined);
    // Vider la liste sans année sélectionnée : sinon les trimestres de l'année
    // précédente restent affichés, et on peut en supprimer un en croyant agir
    // sur une autre année.
    if (yearId) api.listTerms(yearId).then(setTerms).catch(() => undefined);
    else setTerms([]);
    school.listLevels().then(setLevels).catch(() => undefined);
  }, [api, school, yearId]);
  useEffect(load, [load]);

  // sort_order est l'ordre d'affichage voulu : l'appliquer, plutôt que de
  // laisser l'ordre de retour de l'API.
  const sortedTerms = useMemo(
    () => [...terms].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [terms]
  );

  const yearLabel = years.find((y) => y.id === yearId)?.label;

  return (
    <div>
      <PageHeader
        title="Admin pédagogique"
        subtitle="Matières, salles, trimestres et coefficients de bulletin"
        filters={
          <Select
            style={{ width: 150 }} value={yearId} onChange={setYearId} placeholder="Année scolaire"
            options={years.map((y) => ({ value: y.id, label: y.label }))}
          />
        }
      />

      {/* Deux colonnes : quatre réglages indépendants, aucun n'est long. Les
          empiler sur toute la largeur imposait de faire défiler pour rien. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Matières" size="small">
            <InlineCreate label="Ajouter" created="Matière ajoutée" onFinish={(v) => api.createSubject(v)} onDone={load}>
              <Form.Item name="code" rules={[{ required: true, message: "Code requis" }]}>
                <Input placeholder="MATH" aria-label="Code de la matière" style={{ width: 100 }} />
              </Form.Item>
              <Form.Item name="name" rules={[{ required: true, message: "Nom requis" }]}>
                <Input placeholder="Mathématiques" aria-label="Nom de la matière" style={{ width: 180 }} />
              </Form.Item>
            </InlineCreate>
            {/* Liste avec action explicite, et non des Tag qui supprimaient au
                clic : rien n'annonçait qu'un clic détruisait la matière. */}
            <List size="small" dataSource={subjects}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune matière" /> }}
              renderItem={(s: any) => (
                // Matière du programme marocain (s.locked) : pas de suppression.
                <List.Item actions={s.locked ? [] : [
                  <Popconfirm key="d" title="Supprimer cette matière ?"
                    description="Les évaluations et coefficients liés seront affectés."
                    okText="Supprimer" cancelText="Annuler"
                    onConfirm={() => api.deleteSubject(s.id).then(load).catch(apiError)}>
                    <Button size="small" type="text" danger>Supprimer</Button>
                  </Popconfirm>]}>
                  <Space size={8} wrap>
                    <Typography.Text code style={{ fontFamily: FONT_MONO }}>{s.code}</Typography.Text>
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    {s.locked && <ProgrammeTag />}
                  </Space>
                </List.Item>)} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Salles" size="small">
            <InlineCreate label="Ajouter" created="Salle ajoutée" onFinish={(v) => api.createRoom(v)} onDone={load}>
              <Form.Item name="name" rules={[{ required: true, message: "Nom requis" }]}>
                <Input placeholder="Salle 101" aria-label="Nom de la salle" style={{ width: 130 }} />
              </Form.Item>
              <Form.Item name="capacity">
                <InputNumber placeholder="Capacité" aria-label="Capacité de la salle" min={1} />
              </Form.Item>
            </InlineCreate>
            <List size="small" dataSource={rooms}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune salle" /> }}
              renderItem={(r: any) => (
                <List.Item actions={[
                  <Popconfirm key="d" title="Supprimer cette salle ?" okText="Supprimer" cancelText="Annuler"
                    onConfirm={() => api.deleteRoom(r.id).then(load).catch(apiError)}>
                    <Button size="small" type="text" danger>Supprimer</Button>
                  </Popconfirm>]}>
                  <Space size={8}>
                    <span style={{ fontWeight: 500 }}>{r.name}</span>
                    {/* « (30) » ne disait pas de quoi il s'agissait. */}
                    <Typography.Text type="secondary">
                      {r.capacity ? `${r.capacity} places` : "capacité non précisée"}
                    </Typography.Text>
                  </Space>
                </List.Item>)} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Trimestres" size="small"
            // L'année pilotant cette liste se choisit en haut de page : la
            // rappeler ici évite de supprimer un trimestre en croyant être sur
            // une autre année.
            extra={yearLabel ? <Typography.Text type="secondary">{yearLabel}</Typography.Text> : null}>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              Périodes du bulletin. Les échéances de paiement se règlent séparément, dans{" "}
              <Link to="/paiements">Paiements</Link>.
            </Typography.Paragraph>
            <InlineCreate label="Ajouter" created="Trimestre ajouté" onFinish={(v) => api.createTerm({ ...v, school_year_id: yearId })} onDone={load}>
              <Form.Item name="label" rules={[{ required: true, message: "Libellé requis" }]}>
                <Input placeholder="Trimestre 1" aria-label="Libellé du trimestre" style={{ width: 140 }} />
              </Form.Item>
              {/* Le « 1 » nu n'indiquait pas ce qu'il réglait. */}
              <Form.Item name="sort_order" initialValue={1}>
                <InputNumber min={1} aria-label="Ordre d'affichage" addonBefore="Ordre" style={{ width: 120 }} />
              </Form.Item>
            </InlineCreate>
            <List size="small" dataSource={sortedTerms}
              locale={{
                emptyText: (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={yearId ? "Aucun trimestre pour cette année" : "Sélectionnez une année scolaire"} />
                ),
              }}
              renderItem={(t: any) => (
                <List.Item actions={[
                  <Popconfirm key="d" title="Supprimer ce trimestre ?" okText="Supprimer" cancelText="Annuler"
                    onConfirm={() => api.deleteTerm(t.id).then(load).catch(apiError)}>
                    <Button size="small" type="text" danger>Supprimer</Button>
                  </Popconfirm>]}>
                  <Space size={8}>
                    <Typography.Text code style={{ fontFamily: FONT_MONO }}>{t.sort_order}</Typography.Text>
                    <span style={{ fontWeight: 500 }}>{t.label}</span>
                  </Space>
                </List.Item>)} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <CoefficientsCard api={api} levels={levels} subjects={subjects} />
        </Col>
      </Row>
    </div>
  );
};

/**
 * CoefficientsCard — poids de chaque matière dans le bulletin d'un niveau.
 * Isolée : c'est le seul réglage à porter un état d'édition (la grille de
 * coefficients) et un enregistrement explicite.
 */
const CoefficientsCard: React.FC<{
  api: ReturnType<typeof usePedagogyApi>;
  levels: Level[];
  subjects: any[];
}> = ({ api, levels, subjects }) => {
  const [levelId, setLevelId] = useState<number>();
  const [coefs, setCoefs] = useState<Record<number, number>>({});
  // Matières du programme marocain pour ce niveau : coefficient réglable,
  // mais jamais à 0 (ce serait retirer la matière, refusé par le backend).
  const [locked, setLocked] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!levelId) { setCoefs({}); setLocked(new Set()); return; }
    api.getLevelSubjects(levelId).then((items) => {
      const map: Record<number, number> = {};
      items.forEach((i) => { map[i.subject_id] = i.coefficient; });
      setCoefs(map);
      setLocked(new Set(items.filter((i) => i.locked).map((i) => i.subject_id)));
    }).catch(() => { setCoefs({}); setLocked(new Set()); });
  }, [api, levelId]);

  const save = () => {
    setSaving(true);
    // Un coefficient à 0 vaut « matière non évaluée à ce niveau » : on ne
    // l'envoie pas, le backend remplaçant la grille entière.
    api.setLevelSubjects(levelId!,
      Object.entries(coefs).filter(([, c]) => c > 0).map(([sid, c]) => ({ subject_id: Number(sid), coefficient: c })))
      .then(() => message.success("Coefficients enregistrés"))
      .catch(apiError)
      .finally(() => setSaving(false));
  };

  return (
    <Card title="Coefficients de bulletin" size="small"
      extra={
        <Select placeholder="Niveau" style={{ width: 200 }} value={levelId} onChange={setLevelId} allowClear
          options={levels.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))} />
      }>
      {!levelId ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez un niveau" />
      ) : subjects.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Créez d'abord des matières" />
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Poids de chaque matière dans la moyenne générale. 0 = matière non évaluée à ce niveau.
          </Typography.Paragraph>
          <Row gutter={[8, 8]}>
            {subjects.map((s) => (
              <Col xs={12} md={8} key={s.id}>
                <Space size={6}>
                  <Typography.Text code style={{ fontFamily: FONT_MONO }}>{s.code}</Typography.Text>
                  {locked.has(s.id) && (
                    <Tooltip title="Matière du programme marocain : coefficient modifiable, retrait impossible">
                      <LockOutlined aria-label="Matière du programme marocain" style={{ color: "var(--app-muted)" }} />
                    </Tooltip>
                  )}
                  <InputNumber
                    size="small" min={locked.has(s.id) ? 0.5 : 0} step={0.5} style={{ width: 72 }}
                    aria-label={`Coefficient de ${s.name}`}
                    value={coefs[s.id] ?? 0}
                    onChange={(v) => setCoefs((prev) => ({ ...prev, [s.id]: v ?? (locked.has(s.id) ? 0.5 : 0) }))}
                  />
                </Space>
              </Col>
            ))}
          </Row>
          <Button type="primary" size="small" loading={saving} style={{ marginTop: 12 }} onClick={save}>
            Enregistrer les coefficients
          </Button>
        </>
      )}
    </Card>
  );
};

export default PedagogyAdminPage;
